import { memo, useCallback, useMemo } from 'react';

import { WorkspaceAgentBoardTemplatesFlow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/WorkspaceAgentBoardTemplatesFlow';
import { PrivateWorkspaceAgentBoardPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentBoardPillChip';
import {
  getWorkspaceAgentBoardTemplateOptionById,
  getWorkspaceAgentBoardTemplateStructuredDisplay,
  WORKSPACE_AGENT_BOARD_TEMPLATE_OPTIONS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentBoardTemplates';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import { useWorkspaceAgentComposerSeed } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import {
  useWorkspaceAgentPanelStore,
  type WorkspaceAgentBoardTemplatesFlowBoard,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

export type PrivateWorkspaceAgentBoardTemplatesFlowProps = {
  board: WorkspaceAgentBoardTemplatesFlowBoard;
};

export const PrivateWorkspaceAgentBoardTemplatesFlow = memo(
  ({ board }: PrivateWorkspaceAgentBoardTemplatesFlowProps) => {
    const { seedComposer } = useWorkspaceAgentComposerSeed();
    const { blockIfCreditsDepleted, isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
    const clearBoardTemplatesFlowBoard = useWorkspaceAgentPanelStore((state) => state.clearBoardTemplatesFlowBoard);
    const focusComposer = useWorkspaceAgentPanelStore((state) => state.focusComposer);

    const boardChip = useMemo(
      () => <PrivateWorkspaceAgentBoardPillChip pill={{ boardId: board.id, id: board.id, type: 'board' }} />,
      [board.id],
    );

    const handleSelect = useCallback(
      (optionId: string) => {
        const option = getWorkspaceAgentBoardTemplateOptionById(optionId);

        if (!option || blockIfCreditsDepleted()) {
          return;
        }

        const isSentImmediately = option.kind === 'send';

        seedComposer(option.getPrompt(board.id), {
          boardPill: { boardId: board.id, title: board.title },
          sendImmediately: isSentImmediately,
          source: 'board_menu',
          structuredDisplay: isSentImmediately
            ? getWorkspaceAgentBoardTemplateStructuredDisplay({ boardTitle: board.title, option })
            : undefined,
          workspaceId: board.workspaceId,
        });
        clearBoardTemplatesFlowBoard();
      },
      [blockIfCreditsDepleted, board.id, board.title, board.workspaceId, clearBoardTemplatesFlowBoard, seedComposer],
    );

    const handleCancel = useCallback(() => {
      clearBoardTemplatesFlowBoard();
      focusComposer();
    }, [clearBoardTemplatesFlowBoard, focusComposer]);

    return (
      <WorkspaceAgentBoardTemplatesFlow
        boardChip={boardChip}
        boardTitle={board.title}
        isDisabled={isCreditsDepleted}
        onCancel={handleCancel}
        onSelect={handleSelect}
        options={WORKSPACE_AGENT_BOARD_TEMPLATE_OPTIONS}
      />
    );
  },
);

PrivateWorkspaceAgentBoardTemplatesFlow.displayName = 'PrivateWorkspaceAgentBoardTemplatesFlow';
